import { Dialog, useDialog } from 'components';
import DataTable, { DataTableColumn } from 'components/tables/data-table';
import { useSizeTypes } from 'contexts';
import { FC, useCallback, useState } from 'react';
import { DuplicateSizeTypeCodeError, SizeTypeSaveError } from 'services/size-types';
import { EquipmentType, SizeTypeLookupOption } from 'types';
import './size-types.scss';

const columns: DataTableColumn[] = [
  { field: 'code', label: 'Code', width: 80 },
  { field: 'label', label: 'Label', width: 200 },
  { field: 'equipmentType', label: 'Equipment Type', width: 120 },
  { field: 'active', label: 'Active', width: 60 },
  { field: 'reefer', label: 'Reefer', width: 60 }
];

const blankDraft = (): Partial<SizeTypeLookupOption> => ({
  code: '',
  label: '',
  equipmentType: EquipmentType.CONTAINER,
  active: true,
  reefer: false
});

export const SizeTypesTab: FC = () => {
  const { sizeTypeList, saveSizeType, deleteSizeType } = useSizeTypes();
  const { ref, openDialog, closeDialog } = useDialog({});
  const [selectedRows, setSelectedRows] = useState<SizeTypeLookupOption[]>([]);
  const [draft, setDraft] = useState<Partial<SizeTypeLookupOption>>(blankDraft);
  const [error, setError] = useState<string>('');
  /* refusals from a (multi-row) delete, shown above the grid; a refused row is never reported as deleted */
  const [deleteErrors, setDeleteErrors] = useState<string[]>([]);

  const fetchData = useCallback(() => Promise.resolve(sizeTypeList), [sizeTypeList]);

  /* seed the draft (managed fields only), clear any prior error, then open the (stable) dialog */
  const openFor = useCallback(
    (option?: SizeTypeLookupOption) => {
      setError('');
      setDraft(
        option
          ? {
              id: option.id,
              code: option.code,
              label: option.label,
              equipmentType: option.equipmentType,
              active: option.active,
              reefer: !!option.reefer
            }
          : blankDraft()
      );
      openDialog();
    },
    [openDialog]
  );

  const handleSave = useCallback(async () => {
    try {
      await saveSizeType(draft);
      closeDialog();
    } catch (e) {
      /* duplicate code on create — keep the dialog open and prompt to reactivate instead */
      if (e instanceof DuplicateSizeTypeCodeError) {
        setError(`Code "${draft.code}" already exists. Edit the existing entry and set it active to reactivate it.`);
      } else if (e instanceof SizeTypeSaveError) {
        /* the server's own reason, e.g. the fleet mappings that still suggest this code */
        setError(e.message);
      } else {
        setError('Something went wrong saving the size type. Please try again.');
      }
    }
  }, [draft, saveSizeType, closeDialog]);

  const handleDelete = useCallback(async () => {
    const rows = selectedRows.filter(r => !!r.id);
    const results = await Promise.allSettled(rows.map(r => deleteSizeType(r.id)));
    setDeleteErrors(
      results.flatMap((result, i) =>
        result.status === 'rejected'
          ? [
              `${rows[i].code}: ${
                result.reason instanceof SizeTypeSaveError ? result.reason.message : 'could not be deactivated'
              }`
            ]
          : []
      )
    );
  }, [selectedRows, deleteSizeType]);

  return (
    <div id="size-types">
      <Dialog ref={ref} closeDialog={closeDialog}>
        <div className="dialog-header">
          <h3>{draft.id ? 'Edit Size Type' : 'New Size Type'}</h3>
        </div>
        <div className="dialog-body">
          <div>
            <p className="margin-top-16px">Code</p>
            {/* code is immutable post-create (UNIQUE + persisted snapshot) — read-only on edit */}
            <input
              type="text"
              value={draft.code}
              disabled={!!draft.id}
              onChange={({ target }) => setDraft({ ...draft, code: target.value })}
            />
          </div>
          <div>
            <p className="margin-top-16px">Label</p>
            <input
              type="text"
              value={draft.label}
              onChange={({ target }) => setDraft({ ...draft, label: target.value })}
            />
          </div>
          <div>
            <p className="margin-top-16px">Equipment Type</p>
            <select
              value={draft.equipmentType}
              onChange={({ target }) => setDraft({ ...draft, equipmentType: target.value as EquipmentType })}
            >
              {Object.values(EquipmentType).map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="d-flex align-items-center margin-top-16px">
            <input
              id="size-type-reefer"
              type="checkbox"
              checked={!!draft.reefer}
              onChange={({ target }) => setDraft({ ...draft, reefer: target.checked })}
            />
            <label htmlFor="size-type-reefer" className="margin-left-8px mb-0">
              Reefer (moves record temperature and ventilation)
            </label>
          </div>
          {!!draft.id && (
            <div className="d-flex align-items-center margin-top-16px">
              <input
                type="checkbox"
                checked={!!draft.active}
                onChange={({ target }) => setDraft({ ...draft, active: target.checked })}
              />
              <p className="margin-left-8px mb-0">Active</p>
            </div>
          )}
          {!!error && <p className="text-danger margin-top-16px mb-0">{error}</p>}
          <div className="d-flex justify-content-between margin-top-16px">
            <button className="btn-outline" onClick={closeDialog}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={!draft.code || !draft.label || !draft.equipmentType}
              onClick={handleSave}
            >
              <span>Save</span>
            </button>
          </div>
        </div>
      </Dialog>
      <div className="d-flex justify-content-end g-3 align-items-center mb-2">
        {!!selectedRows.length && (
          <>
            <button className="btn-danger" onClick={handleDelete}>
              delete
            </button>
            <button
              className="btn-outline"
              disabled={selectedRows.length !== 1}
              onClick={() => openFor(selectedRows[0])}
            >
              edit
            </button>
          </>
        )}
        <button className="btn-primary" onClick={() => openFor()}>
          NEW SIZE TYPE
        </button>
      </div>
      {!!deleteErrors.length && (
        <div className="text-danger mb-2" role="alert">
          {deleteErrors.map(message => (
            <p key={message} className="mb-0">
              {message}
            </p>
          ))}
        </div>
      )}
      <DataTable
        key={`size-types-${sizeTypeList.length}`}
        tableClass="t-height"
        fetchData={fetchData}
        onRowSelectionModelChange={setSelectedRows}
        initialPage={{ sortField: 'label', sortDirection: 'asc', pageSize: 25 }}
        columns={columns}
        initialColumns={columns}
        rowClassName={row => (row.active ? '' : 'deleted')}
        clientSidePagination
        csvFilename="SizeTypes.csv"
        hideTableControls
        checkboxSelection
      />
    </div>
  );
};
